import { defineStore } from 'pinia';
import vueCookie from 'vue-cookies';
import localStorageHelper from '@/helper/localStorageHelper';
import { jwtDecode } from 'jwt-decode';
import { authService } from '@/services/auth';
import axios from '@/axios';
import filters from '@/filters/filters';
import { $settings } from '@/provider/settings';
import { useRootStore } from '@/store/root';
import router from '@/router';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    profile: localStorageHelper.getProfile(),
    token: localStorage.getItem('token') || '',
    redirect: localStorage.getItem('redirect') || '',
    basicAuthLoggedIn:
      vueCookie.isKey('loggedInBasic') ||
      localStorage.getItem('redirect') ||
      false,
  }),
  getters: {
    basicAuth: (state) => state.basicAuthLoggedIn || false,
  },
  actions: {
    /**
     * login
     */
    async login() {
      await authService.login();

      useRootStore()
        .init(null)
        .finally(() => {
          location.reload();
        });
    },

    checkLogin() {
      const profile = this.token && jwtDecode(this.token);
      if (profile) {
        localStorage.setItem('token', this.token);
        this.profile = profile;
        localStorageHelper.setProfile(JSON.stringify(profile));
      }
    },

    /**
     * Auth Router Resolve
     * @param commit
     * @param to
     * @param $router
     */
    resolve({ to, next, $router }) {
      const { token } = to.query;

      try {
        const profile = token && jwtDecode(token);

        if (profile) {
          this.token = token;
          this.profile = profile;
          localStorage.setItem('token', token);
          localStorageHelper.setProfile(JSON.stringify(profile));

          return next(
            $settings.useBasicAuth
              ? '/basicAuth'
              : this.redirect || '/dashboard/home',
          );
        }
      } catch (error) {
        console.error(error);
        this.profile = {};
        this.token = '';
        this.redirect = '';
        vueCookie.remove('loggedInBasic');
        localStorage.clear();
        return $router.replace(''); // remove TokenId in BrowserHistory
      }
      return next();
    },

    async loginBasic({ credentials }) {
      const encodedData = `Basic ${window.btoa(
        `${credentials.username}:${credentials.password}`,
      )}`;
      return axios
        .post(
          [$settings.middlewareURI, 'auths', 'loginSAP'].join('/'),
          {},
          { headers: { 'x-authorization': encodedData } },
        )
        .then((response) => {
          if (response.status < 400) {
            this.basicAuthLoggedIn = true;
            vueCookie.set('loggedInBasic', true, 0);
            localStorageHelper.setItem('loggedInBasic', true);
            useRootStore()
              .init(null)
              .then(() => {
                location.reload();
              });
          }
        })
        .catch((error) => {
          if (error.response.status === 401) {
            filters.showMessage(
              'error',
              'WX_GENERAL_LOGIN_FAILED_CHECK_CREDENTIALS',
            );
          }

          return false;
        });
    },

    /**
     * logout
     */
    async logout() {
      const logoutSuccess = await authService.logout();
      if (logoutSuccess) {
        this.profile = {};
        this.token = '';
        this.redirect = '';

        vueCookie.remove('loggedInBasic');
        localStorage.clear();
        await router.push({ name: 'login' });
      }
    },

    setToken(token) {
      localStorage.setItem('token', token);
      this.token = token;
    },

    setProfile(profile) {
      this.profile = profile;
      localStorageHelper.setProfile(JSON.stringify(profile));
    },

    setRedirect(redirect) {
      if (redirect) {
        this.redirect = redirect;
      }
    },

    setBasicAuth(loggedIn) {
      this.basicAuthLoggedIn = loggedIn;
      vueCookie.set('loggedInBasic', loggedIn, 0);
      localStorageHelper.setItem('loggedInBasic', loggedIn);
    },
  },
});
